import React, { useState } from "react";
import { Nav, NavDropdown, Container, Navbar } from "react-bootstrap";
import { FaFacebook, FaInstagram, FaYoutube } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import useUnreadCount from "../../hooks/useUnreadCount";
import NotificationBell from "../backend/NotificationBell";
import OwnerNotificationBell from "../backend/OwnerNotificationBell";
import RenterNotificationBell from "../backend/RenterNotificationBell";

export default function Header() {
  const { logout, currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const unreadCount = useUnreadCount();
  const [isNavbarOpen, setNavbarOpen] = useState(false);

  // Function to check if a route is active with improved logic
  const isActiveRoute = (path) => {
    const currentPath = location.pathname;

    // Exact match
    if (currentPath === path) {
      return true;
    }

    // Exact match handles root path now

    // Handle nested routes (e.g., /waivers/view-waivers should be active when on /waivers/*)
    if (path.startsWith("/waivers/") && currentPath.startsWith("/waivers/")) {
      return true;
    }

    // Handle sub-routes (e.g., if path is /about and current is /about/something)
    if (currentPath.startsWith(path + "/")) {
      return true;
    }

    return false;
  };

  // Helper function to get the current route color
  const getRouteColor = (path, defaultColor = "#ffffff") => {
    return isActiveRoute(path) ? "#ddb467" : defaultColor;
  };

  // Helper function to get the current route font weight
  const getRouteFontWeight = (path) => {
    return isActiveRoute(path) ? "600" : "normal";
  };

  // Main navigation routes
  const mainRoutes = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/referral-program", label: "Referral Program", id: "download-app-newLink" },

    { path: "/get-verified", label: "Get Verified" },
    { path: "/contact", label: "Contact Us" },
    { path: "/how-it-works", label: "How It Works" },
    { path: "/faq", label: "FAQ" },
    // { path: "/blog", label: "Blog" },
    { path: "/download-app", label: "Get App", id: "download-app-newLink" }
  ];

  // Dropdown menu routes
  const dropdownRoutes = [
    { path: "/cancellation", label: "Cancellation" },
    { path: "/pricing", label: "Pricing" },
    { path: "/privacy-policy", label: "Privacy Policy" },
    { path: "/cookie-policy", label: "Cookie Policy" },
    { path: "/client-relationship", label: "Client Relationship" },
    { path: "https://tpwd.texas.gov/regulations/outdoor-annual/", label: "TPWD Outdoor Annual", external: true },
    { path: "https://www.takemefishing.org/", label: "Fishing License", external: true },
    { path: "https://www.boatus.org/", label: "Boaters Education", external: true },
    { path: "https://www.youtube.com/watch?v=fLtisa4qC10", label: "Boating Safety Video", external: true },
    { path: "https://www.youtube.com/watch?v=IPAYWGsYJ5M", label: "Launch And Load The Boat", external: true },
    { path: "https://tpwd.texas.gov/publications/pwdpubs/media/pwd_bk_l2000_0014.pdf#:~:text=1%2C%201975.&text=It%20is%20the%20duty%20of,laws%20relating%20to%20water%20safety.", label: "Texas Water Safety Act", external: true },
    { path: "/launch-load-checklists", label: "Launch & Load Checklists" },
    { path: "/account-deletion", label: "Account Deletion" }
  ];

  const waiverRoutes = [
    { path: "/my-waiver", label: "My Signed Waivers" },
    { path: "/waivers/view-waivers", label: "Submit Waivers" },
  ];

  const handleNavbarToggle = () => {
    setNavbarOpen(!isNavbarOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch {
      console.log("Failed to log out");
    }
  };

  // Render a navigation link
  const renderNavLink = (route) => {
    const { path, label, id, external } = route;

    if (external) {
      return (
        <Link
          key={path}
          to={path}
          className="text-dark"
          target="_blank"
        >
          {label}
        </Link>
      );
    }

    return (
      <Link
        key={path}
        onClick={() => setNavbarOpen(false)}
        to={path}
        className="cs-tx-light nav-link"
        id={id}
        style={{
          color: getRouteColor(path, "#ffffff"),
          fontWeight: getRouteFontWeight(path),
          transition: "color 0.3s ease",
        }}
        onMouseEnter={(e) => {
          if (!isActiveRoute(path)) {
            e.target.style.color = "#ddb467";
          }
        }}
        onMouseLeave={(e) => {
          if (!isActiveRoute(path)) {
            e.target.style.color = "#ffffff";
          }
        }}
      >
        {label}
      </Link>
    );
  };

  // Render a dropdown link
  const renderDropdownLink = (route) => {
    const { path, label, external } = route;

    if (external) {
      return (
        <Link
          key={path}
          to={path}
          className="text-dark"
          target="_blank"
        >
          {label}
        </Link>
      );
    }

    return (
      <Link
        key={path}
        to={path}
        className="text-dark"
        style={{
          color: isActiveRoute(path) ? "#ddb467" : "#000000",
          fontWeight: isActiveRoute(path) ? "600" : "normal",
          transition: "color 0.3s ease",
        }}
        onMouseEnter={(e) => {
          if (!isActiveRoute(path)) {
            e.target.style.color = "#ddb467";
          }
        }}
        onMouseLeave={(e) => {
          if (!isActiveRoute(path)) {
            e.target.style.color = "#000000";
          }
        }}
      >
        {label}
      </Link>
    );
  };

  return (
    <Navbar
      collapseOnSelect
      expand="lg"
      expanded={isNavbarOpen}
      className="bg-body-tertiary navbar-dark header cs-bg-dark"
    >
      <Container style={{ maxWidth: "1440px" }}>
        <Navbar.Brand>
          <Link to={"/"}>
            <img
              style={{ backgroundColor: "#121212" }}
              width={150}
              src="/logo.png"
              alt="Logo"
            />
          </Link>
        </Navbar.Brand>
        <Navbar.Toggle
          aria-controls="responsive-navbar-nav"
          onClick={handleNavbarToggle}
        />
        <Navbar.Collapse
          id="responsive-navbar-nav"
          className="justify-content-between"
        >
          <Nav className="cs-nav" style={{ alignItems: "center" }}>
            {/* Render main navigation links */}
            {mainRoutes.slice(0, 3).map(renderNavLink)}

            <NavDropdown
              title="Waivers"
              className="cs-tx-light"
              id="waivers-nav-dropdown"
            >
              <div className="d-flex flex-column dropdown-div">
                {waiverRoutes.map(renderDropdownLink)}
              </div>
            </NavDropdown>

            {mainRoutes.slice(3).map(renderNavLink)}

            <NavDropdown
              title="More"
              className="cs-tx-light"
              id="collapsible-nav-dropdown"
            >
              <div className="d-flex flex-column dropdown-div">
                {/* Render dropdown links */}
                {dropdownRoutes.map(renderDropdownLink)}
              </div>
            </NavDropdown>

            {currentUser ? (
              <>
                <Link
                  to="/dashboard"
                  className="cs-tx-light nav-link d-flex align-items-center"
                  style={{
                    color: isActiveRoute("/dashboard") ? "#ddb467" : "#ffffff",
                    fontWeight: isActiveRoute("/dashboard") ? "600" : "normal",
                    transition: "color 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActiveRoute("/dashboard")) {
                      e.target.style.color = "#ddb467";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActiveRoute("/dashboard")) {
                      e.target.style.color = "#ffffff";
                    }
                  }}
                >
                  Dashboard
                  {unreadCount > 0 && (
                    <span
                      className="ms-2"
                      style={{
                        backgroundColor: '#ff4d4f',
                        color: '#fff',
                        borderRadius: '50%',
                        minWidth: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        padding: '0 4px'
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </Link>
                {(userRole === "Admin" ||
                  userRole === "Boat Owner" ||
                  userRole === "Renter") && (
                  <ul className="navbar-nav header-notifications mb-0 p-0">
                    {userRole === "Admin" && <NotificationBell />}
                    {userRole === "Boat Owner" && <OwnerNotificationBell />}
                    {userRole === "Renter" && <RenterNotificationBell />}
                  </ul>
                )}
                <Link
                  onClick={handleLogout}
                  className="cs-tx-light nav-link"
                  style={{
                    color: "#ffffff",
                    fontWeight: "normal",
                    transition: "color 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#ddb467";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "#ffffff";
                  }}
                >
                  Logout
                </Link>
              </>
            ) : (
              <Link
                to="/auth"
                className="cs-tx-light nav-link"
                style={{
                  color: isActiveRoute("/auth") ? "#ddb467" : "#ffffff",
                  fontWeight: isActiveRoute("/auth") ? "600" : "normal",
                  transition: "color 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isActiveRoute("/auth")) {
                    e.target.style.color = "#ddb467";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActiveRoute("/auth")) {
                    e.target.style.color = "#ffffff";
                  }
                }}
              >
                Login
              </Link>
            )}
          </Nav>
          <Nav>
            <div className="social-icons">
              <Link
                to="https://www.facebook.com/BBRboatrentals/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaFacebook />
              </Link>
              <Link
                to="https://www.instagram.com/bbrboatrentals/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram />
              </Link>
              <Link
                to="https://www.youtube.com/channel/UCgOQSUpJj1MkRkt6-tJZJFg"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaYoutube />
              </Link>
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}
